import api from "@client/content/lib/api";
import { type ModuleApiInfo } from "@client/content/selectors";
import { type HttpApiResponse } from "@client/types/Api";
import { type GetHistoryArgs } from "@common/components/Activity/History";
import { System } from "@common/declarations";
import Network from "@common/modules/network/types";
import { Users } from "@common/modules/user/types";
import { type MemberAffiliateDashboard } from "./types";

export default {
  init: (apiInfo: ModuleApiInfo, join?: boolean): Promise<HttpApiResponse<MemberAffiliateDashboard.Api.InitResponse>> =>
    api.get(apiInfo, `/api/site/v1/modules/member/affiliate/dashboard`, { join }),

  getLeaderboard: (
    apiInfo: ModuleApiInfo,
    id: string
  ): Promise<HttpApiResponse<MemberAffiliateDashboard.Api.LeaderBoard>> =>
    api.get(apiInfo, `/api/site/v1/modules/member/affiliate/dashboard/leaderboard/${id}`),

  getLeaderboardPage: (
    apiInfo: ModuleApiInfo,
    id: string,
    page: number,
    size: number
  ): Promise<HttpApiResponse<MemberAffiliateDashboard.Api.IncentiveLeaderboardItem[]>> =>
    api.get(apiInfo, `/api/site/v1/modules/member/affiliate/dashboard/leaderboard/${id}/items`, { page, size }),

  getLeaderboardItem: (
    apiInfo: ModuleApiInfo,
    id: string
  ): Promise<HttpApiResponse<MemberAffiliateDashboard.Api.IncentiveLeaderboardItem>> =>
    api.get(apiInfo, `/api/site/v1/modules/member/affiliate/dashboard/leaderboard/${id}/current`),

  getLeaderboardCount: (apiInfo: ModuleApiInfo, id: string): Promise<HttpApiResponse<number>> =>
    api.get(apiInfo, `/api/site/v1/modules/member/affiliate/dashboard/leaderboard/${id}/count`),

  getAffiliateDashboard: (
    apiInfo: ModuleApiInfo,
    cycleEndDate: string
  ): Promise<HttpApiResponse<Users.Api.DashboardAffiliate>> =>
    api.get(apiInfo, `/api/site/v1/modules/member/affiliate/dashboard/profile`, { cycleEndDate }, "memory"),

  getHistory: (apiInfo: ModuleApiInfo, data: GetHistoryArgs): Promise<HttpApiResponse<System.Api.HistoryItem[]>> =>
    api.get(apiInfo, `/api/site/v1/modules/member/affiliate/dashboard/history`, data),

  getDownline: (
    apiInfo: ModuleApiInfo,
    data: Omit<Network.Api.GetGenealogyDownlineRequest, "currentUserId">
  ): Promise<HttpApiResponse<Network.Api.GenealogyItem[]>> =>
    api.get(apiInfo, `/api/site/v1/modules/member/affiliate/dashboard/downline`, data),

  getUpline: (
    apiInfo: ModuleApiInfo,
    data: Omit<Network.Api.GetGenealogyUplineRequest, "currentUserId">
  ): Promise<HttpApiResponse<Network.Api.GenealogyItem[]>> =>
    api.get(apiInfo, `/api/site/v1/modules/member/affiliate/dashboard/upline`, data),

  getPayoutCycles: (
    apiInfo: ModuleApiInfo,
    data: Network.Payouts.Api.GetCyclesRequest
  ): Promise<HttpApiResponse<Network.Payouts.Api.Cycle[]>> =>
    api.get(apiInfo, `/api/site/v1/modules/member/affiliate/dashboard/payouts`, data, "memory"),

  getCycleAggregates: (
    apiInfo: ModuleApiInfo,
    cycleEndDate: string
  ): Promise<HttpApiResponse<MemberAffiliateDashboard.Api.CycleAggregate[]>> =>
    api.get(apiInfo, `/api/site/v1/modules/member/affiliate/dashboard/cycles/aggregates`, { cycleEndDate }, "memory"),

  getCycleSharePools: (
    apiInfo: ModuleApiInfo,
    cycleEndDate: string
  ): Promise<HttpApiResponse<MemberAffiliateDashboard.Api.CycleSharePool[]>> =>
    api.get(apiInfo, `/api/site/v1/modules/member/affiliate/dashboard/cycles/sharePools`, { cycleEndDate }, "memory"),

  audit: {
    commission: {
      getAudit: (
        apiInfo: ModuleApiInfo,
        data: Network.CommissionRules.Api.GetCommissionAuditDataRequest
      ): Promise<HttpApiResponse<Network.CommissionRules.Api.CommissionAuditData>> =>
        api.get(apiInfo, `/api/site/v1/modules/member/affiliate/dashboard/cycles/commission/audit`, data),
      getHierarchyUserVolume: (
        apiInfo: ModuleApiInfo,
        data: Network.CommissionRules.Api.GetCommissionAuditDataRequest
      ): Promise<HttpApiResponse<Network.CommissionRules.Api.GenealogyHierarchyUserVolume[]>> =>
        api.get(apiInfo, `/api/site/v1/modules/member/affiliate/dashboard/cycles/user/volume`, data),
      getHierarchyVolumeTierPoints: (
        apiInfo: ModuleApiInfo,
        data: Network.CommissionRules.Api.GetCommissionAuditDataRequest
      ): Promise<HttpApiResponse<Network.CommissionRules.Api.GenealogyHierarchyUserVolume[]>> =>
        api.get(apiInfo, `/api/site/v1/modules/member/affiliate/dashboard/cycles/commission/volumeTierPoints`, data),
      getVolume: (
        apiInfo: ModuleApiInfo,
        data: Network.CommissionRules.Api.GetCommissionAuditDataRequest
      ): Promise<HttpApiResponse<Network.CommissionRules.Api.CommissionVolume[]>> =>
        api.get(apiInfo, `/api/site/v1/modules/member/affiliate/dashboard/cycles/commission/volume`, data)
    },
    download: {
      commissionUserVolume: (
        apiInfo: ModuleApiInfo,
        args: Network.CommissionRules.Api.GetCommissionAuditDataRequest
      ): Promise<any> =>
        api.download(
          apiInfo,
          `/api/site/v1/modules/member/affiliate/dashboard/cycles/hierarchy/userVolume/download`,
          args
        ),
      commissionVolume: (
        apiInfo: ModuleApiInfo,
        args: Network.CommissionRules.Api.GetCommissionAuditDataRequest
      ): Promise<any> =>
        api.download(apiInfo, `/api/site/v1/modules/member/affiliate/dashboard/cycles/commissionVolume/download`, args),
      volumeTierPoints: (
        apiInfo: ModuleApiInfo,
        args: Network.CommissionRules.Api.GetCommissionAuditDataRequest
      ): Promise<any> =>
        api.download(apiInfo, `/api/site/v1/modules/member/affiliate/dashboard/cycles/volumeTierPoints/download`, args)
    },
    user: {
      getVolume: (
        apiInfo: ModuleApiInfo,
        cycleEndDate: string | Date
      ): Promise<HttpApiResponse<Network.CommissionRules.Api.UserVolume[]>> =>
        api.get(apiInfo, `/api/site/v1/modules/member/affiliate/dashboard/cycles/point/volume`, {
          cycleEndDate,
          volumeTypeKey: "PersonalPoints"
        })
    }
  }
};
